import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, Copy, Search } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  buildCourseSignupLink,
  fetchLinkCourseStats,
  setCourseFreeViaLink,
  type LinkCourseStats,
} from '@/api/link-courses';

export const Route = createFileRoute('/admin/free-courses')({
  component: FreeCoursesPage,
});

const QUERY_KEY = ['admin', 'link-course-stats'];
const FREE_ONLY = 'free_only';

function FreeCoursesPage() {
  const [search, setSearch] = useState('');
  // Quick filter: only courses tagged FREE_ONLY in ClickUp (access_level 'free_only', migration 684).
  const [freeOnly, setFreeOnly] = useState(false);
  const queryClient = useQueryClient();

  const statsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchLinkCourseStats,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  const toggle = useMutation({
    mutationFn: ({ courseId, on }: { courseId: string; on: boolean }) =>
      setCourseFreeViaLink(courseId, on),
    onSuccess: (_d, { on }) => {
      toast.success(on ? 'Course is now free via link' : 'Course is no longer free via link');
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Could not change this course'),
  });

  const all = statsQuery.data ?? [];
  const freeOnlyCount = all.filter((r) => r.access_level === FREE_ONLY).length;
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = all.filter(
      (r) =>
        (!freeOnly || r.access_level === FREE_ONLY) &&
        (!q || r.name.toLowerCase().includes(q) || r.clickup_id.toLowerCase().includes(q)),
    );
    // Switched-on courses first, then by name.
    return [...filtered].sort(
      (a, b) => Number(b.free_via_link) - Number(a.free_via_link) || a.name.localeCompare(b.name),
    );
  }, [all, search, freeOnly]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <h1 className="text-lg font-semibold">Free courses</h1>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Switch a course on to give it away through an ad link. Only people who sign up with that
          course’s link can see it. Copy the course’s code into the funnel’s link, or into the
          course variable on the landing page, and add the email, name and Facebook click ID exactly
          as you do for every sign-up today.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Link format:{' '}
          <code className="break-all">{buildCourseSignupLink(window.location.origin, 'CODE')}</code>
        </p>
      </div>

      <div className="px-6 py-3 border-b border-border flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            id="free-courses-search"
            placeholder="Search by course name or ClickUp ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <Button
          id="free-courses-free-only-filter"
          variant={freeOnly ? 'default' : 'outline'}
          size="sm"
          aria-pressed={freeOnly}
          onClick={() => setFreeOnly((v) => !v)}
        >
          Free-only courses ({freeOnlyCount})
        </Button>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4">
        {statsQuery.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : statsQuery.error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {statsQuery.error instanceof Error ? statsQuery.error.message : 'Could not load courses'}
            </AlertDescription>
          </Alert>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Course</th>
                  <th className="px-3 py-2 text-left font-medium">Free via link</th>
                  <th className="px-3 py-2 text-left font-medium">Code</th>
                  <th className="px-3 py-2 text-right font-medium">Signed up</th>
                  <th className="px-3 py-2 text-right font-medium">Added to existing account</th>
                  <th className="px-3 py-2 text-right font-medium">Confirmed</th>
                  <th className="px-3 py-2 text-right font-medium">Started</th>
                  <th className="px-3 py-2 text-right font-medium">Finished</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <CourseRow
                    key={r.course_id}
                    row={r}
                    busy={toggle.isPending && toggle.variables?.courseId === r.course_id}
                    onToggle={(on) => toggle.mutate({ courseId: r.course_id, on })}
                  />
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">
                      {freeOnly && !search
                        ? 'No free-only courses yet.'
                        : `No courses match “${search}”${freeOnly ? ' among free-only courses' : ''}.`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function CourseRow({
  row,
  busy,
  onToggle,
}: {
  row: LinkCourseStats;
  busy: boolean;
  onToggle: (on: boolean) => void;
}) {
  const [copied, setCopied] = useState(false);
  // The code is the course's ClickUp id: the `course=` value in the signup link. The online team
  // copies just this, to build their own links or set the landing page's course variable.
  const code = row.clickup_id;
  const why = !row.eligible
    ? 'Only public, platform-wide courses can be given away'
    : row.status !== 'published'
      ? 'Not published — leads won’t see it until it is'
      : null;

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy — select the code and copy it by hand');
    }
  }

  return (
    <tr className="border-t border-border align-top">
      <td className="px-3 py-2 min-w-[14rem]">
        <div className="font-medium">{row.name}</div>
        {row.access_level && (
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <Badge variant="outline" className="text-[10px]">{row.access_level}</Badge>
          </div>
        )}
        {why && <div className="text-xs text-muted-foreground mt-1">{why}</div>}
      </td>
      <td className="px-3 py-2">
        <Switch
          id={`free-via-link-${row.course_id}`}
          checked={row.free_via_link}
          disabled={busy || (!row.eligible && !row.free_via_link)}
          onCheckedChange={onToggle}
          aria-label={`Free via link: ${row.name}`}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <code className="text-xs font-mono whitespace-nowrap">{code}</code>
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={copy} aria-label={`Copy code ${code}`}>
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{row.signups}</td>
      <td className="px-3 py-2 text-right tabular-nums">{row.added}</td>
      <td className="px-3 py-2 text-right tabular-nums">{row.confirmed}</td>
      <td className="px-3 py-2 text-right tabular-nums">{row.started}</td>
      <td className="px-3 py-2 text-right tabular-nums">{row.finished}</td>
    </tr>
  );
}
